import {APIProvider, Map, Marker, useMap, useMapsLibrary} from "@vis.gl/react-google-maps";
import {useListingsStore} from "../../stores/listingsStore";
import {useShallow} from "zustand/react/shallow";
import {CustomMarker} from "./CustomMarker";
import {useEffect, useRef} from "react";
import {groupByDealership} from "./utils";

// Only re-run the listings search when the map center moves at least this far.
// Keeps trivial nudges — and Google's programmatic pans — from refetching.
const MOVE_THRESHOLD_METERS = 1500

// Great-circle distance between two {lat, lng} points, in metres.
const distanceMeters = (a, b) => {
    const R = 6371000
    const toRad = (d) => (d * Math.PI) / 180
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const lat1 = toRad(a.lat)
    const lat2 = toRad(b.lat)
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
    return 2 * R * Math.asin(Math.sqrt(h))
}

// Google Maps can only center on coordinates, not a zip code. This child runs
// inside <APIProvider>/<Map> (where the hooks are available), geocodes the zip
// via the Geocoding library and pans the map to the result. Rendered as a child
// so the geocoder/map instance are in scope.
const ZipCenterer = ({ zipcode }) => {
    const map = useMap()
    const geocodingLib = useMapsLibrary('geocoding')

    useEffect(function panToZip(){
        if (!map || !geocodingLib || !zipcode) return

        const geocoder = new geocodingLib.Geocoder()
        geocoder.geocode({ address: zipcode }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
                map.panTo(results[0].geometry.location)
            }
        })
    }, [map, geocodingLib, zipcode])

    return null
}

export const ListingsMap = ({ initialCenter }) => {
    // Center of the last search we ran — the threshold is measured against this.
    const lastSearchCenter = useRef(initialCenter)

    const {
        mapListings,
        zipcode,
        setFilters,
    } = useListingsStore(
        useShallow((state) => ({
            mapListings: state.mapListings,
            zipcode: state.filters.location?.zipcode,
            setFilters: state.setFilters,
        }))
    )

    // Re-run the search around the map's new center (the backend keeps its
    // default 100-mile radius, same as the rest of the search). Fires only on
    // user drags — Google's InfoWindow auto-pan uses panToBounds, which does
    // NOT emit dragend — and gates on distance so a small drag doesn't refetch.
    const handleDragEnd = (ev) => {
        const c = ev.map?.getCenter?.()
        if (!c) return

        const next = {lat: c.lat(), lng: c.lng()}
        if (distanceMeters(lastSearchCenter.current, next) < MOVE_THRESHOLD_METERS) return

        lastSearchCenter.current = next
        setFilters({location: {zipcode: null, latitude: next.lat, longitude: next.lng}})
    }

    const dealerships = groupByDealership(mapListings)

    return (
        <div style={{height: '75vh'}}>
            {/* Load `places` too: react-google-autocomplete (calc/deal/settings
                zip inputs) reuses whatever Google Maps script is already on the
                page, so if the map loads Maps without places their suggestions
                break. Including it here keeps the shared instance complete. */}
            <APIProvider
                apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                libraries={['places']}
            >
                <Map
                    mapId={"90689af104efeda0bcca6da5"}
                    disableDefaultUI={true}
                    defaultZoom={11}
                    defaultCenter={initialCenter}
                    onDragend={handleDragEnd}
                >
                    <ZipCenterer zipcode={zipcode}/>
                    {dealerships.map((d) => {
                        return (
                            <CustomMarker
                                key={d.id}
                                dealership={d}
                            />
                        );
                    })}
                </Map>
            </APIProvider>
        </div>
    )
}
